/**
 * Sun position and daylight calculations
 * Based on NOAA solar calculations
 */

interface SunTimes {
  sunrise: Date;
  sunset: Date;
  solarNoon: Date;
}

interface DaylightInfo {
  localTime: Date;
  localTimeString: string;
  isDaylight: boolean;
  sunPosition: "day" | "golden" | "twilight" | "night";
  sunriseTime: string;
  sunsetTime: string;
  hoursUntilSunset?: number;
  hoursUntilSunrise?: number;
}

// Convert degrees to radians
const toRad = (deg: number) => (deg * Math.PI) / 180;

// Convert radians to degrees
const toDeg = (rad: number) => (rad * 180) / Math.PI;

/**
 * Calculate the day of year (1-365/366)
 */
const getDayOfYear = (date: Date): number => {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date.getTime() - start.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
};

/**
 * Calculate approximate timezone offset from longitude
 * Each 15 degrees = 1 hour
 */
export const getTimezoneOffsetFromLongitude = (longitude: number): number => {
  return Math.round(longitude / 15);
};

/**
 * Get local time at a given longitude
 */
export const getLocalTime = (longitude: number): Date => {
  const now = new Date();
  const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
  const offsetHours = getTimezoneOffsetFromLongitude(longitude);
  return new Date(utcTime + offsetHours * 3600000);
};

/**
 * Format time as HH:MM
 */
export const formatLocalTime = (date: Date): string => {
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

/**
 * Calculate sunrise and sunset times for a given location and date
 * Based on NOAA solar calculator algorithms
 */
export const getSunTimes = (lat: number, lng: number, date: Date = new Date()): SunTimes => {
  const dayOfYear = getDayOfYear(date);
  const year = date.getFullYear();

  // Fractional year (in radians)
  const gamma = ((2 * Math.PI) / 365) * (dayOfYear - 1);

  // Equation of time (in minutes)
  const eqTime =
    229.18 *
    (0.000075 +
      0.001868 * Math.cos(gamma) -
      0.032077 * Math.sin(gamma) -
      0.014615 * Math.cos(2 * gamma) -
      0.040849 * Math.sin(2 * gamma));

  // Solar declination (in radians)
  const decl =
    0.006918 -
    0.399912 * Math.cos(gamma) +
    0.070257 * Math.sin(gamma) -
    0.006758 * Math.cos(2 * gamma) +
    0.000907 * Math.sin(2 * gamma) -
    0.002697 * Math.cos(3 * gamma) +
    0.00148 * Math.sin(3 * gamma);

  // Hour angle for sunrise/sunset
  const latRad = toRad(lat);
  const zenith = toRad(90.833); // Official zenith for sunrise/sunset

  const cosHa =
    (Math.cos(zenith) - Math.sin(latRad) * Math.sin(decl)) /
    (Math.cos(latRad) * Math.cos(decl));

  // Check for polar day/night
  let sunrise: Date;
  let sunset: Date;
  let solarNoon: Date;

  if (cosHa > 1) {
    // Polar night - sun never rises
    sunrise = new Date(date);
    sunrise.setHours(0, 0, 0, 0);
    sunset = new Date(date);
    sunset.setHours(0, 0, 0, 0);
  } else if (cosHa < -1) {
    // Polar day - sun never sets
    sunrise = new Date(date);
    sunrise.setHours(0, 0, 0, 0);
    sunset = new Date(date);
    sunset.setHours(23, 59, 59, 999);
  } else {
    const ha = toDeg(Math.acos(cosHa));

    // Solar noon (in minutes from midnight UTC)
    const solarNoonMinutes = 720 - 4 * lng - eqTime;

    // Sunrise and sunset times (in minutes from midnight UTC)
    const sunriseMinutes = solarNoonMinutes - ha * 4;
    const sunsetMinutes = solarNoonMinutes + ha * 4;

    // Convert to Date objects (UTC)
    sunrise = new Date(date);
    sunrise.setUTCHours(0, 0, 0, 0);
    sunrise.setUTCMinutes(sunriseMinutes);

    sunset = new Date(date);
    sunset.setUTCHours(0, 0, 0, 0);
    sunset.setUTCMinutes(sunsetMinutes);

    solarNoon = new Date(date);
    solarNoon.setUTCHours(0, 0, 0, 0);
    solarNoon.setUTCMinutes(solarNoonMinutes);
  }

  return {
    sunrise: sunrise!,
    sunset: sunset!,
    solarNoon: solarNoon!,
  };
};

/**
 * Get complete daylight information for a location
 * Uses local solar time based on longitude for accurate day/night detection
 */
export const getDaylightInfo = (lat: number, lng: number): DaylightInfo => {
  const localTime = getLocalTime(lng);

  // Get local hour as decimal (e.g., 14.5 for 2:30 PM)
  const localHourDecimal = localTime.getHours() + localTime.getMinutes() / 60;

  // Calculate sunrise/sunset in local time
  const dayOfYear = getDayOfYear(localTime);

  // Fractional year (in radians)
  const gamma = ((2 * Math.PI) / 365) * (dayOfYear - 1);

  // Equation of time (in minutes)
  const eqTime =
    229.18 *
    (0.000075 +
      0.001868 * Math.cos(gamma) -
      0.032077 * Math.sin(gamma) -
      0.014615 * Math.cos(2 * gamma) -
      0.040849 * Math.sin(2 * gamma));

  // Solar declination (in radians)
  const decl =
    0.006918 -
    0.399912 * Math.cos(gamma) +
    0.070257 * Math.sin(gamma) -
    0.006758 * Math.cos(2 * gamma) +
    0.000907 * Math.sin(2 * gamma) -
    0.002697 * Math.cos(3 * gamma) +
    0.00148 * Math.sin(3 * gamma);

  // Hour angle for sunrise/sunset
  const latRad = toRad(lat);
  const zenith = toRad(90.833); // Official zenith for sunrise/sunset

  const cosHa =
    (Math.cos(zenith) - Math.sin(latRad) * Math.sin(decl)) /
    (Math.cos(latRad) * Math.cos(decl));

  let sunriseHour: number;
  let sunsetHour: number;
  let isDaylight: boolean;
  let sunPosition: "day" | "golden" | "twilight" | "night";

  if (cosHa > 1) {
    // Polar night - sun never rises
    sunriseHour = 0;
    sunsetHour = 0;
    isDaylight = false;
    sunPosition = "night";
  } else if (cosHa < -1) {
    // Polar day - sun never sets
    sunriseHour = 0;
    sunsetHour = 24;
    isDaylight = true;
    sunPosition = "day";
  } else {
    const ha = toDeg(Math.acos(cosHa));

    // Solar noon in local solar time is approximately 12:00
    // Adjust for equation of time (typically small, a few minutes)
    const solarNoonLocal = 12 - eqTime / 60;

    // Sunrise and sunset in local hours
    sunriseHour = solarNoonLocal - ha / 15;
    sunsetHour = solarNoonLocal + ha / 15;

    // Determine daylight status based on local time
    const goldenHourDuration = 1; // 1 hour
    const twilightDuration = 0.5; // 30 minutes

    if (localHourDecimal >= sunriseHour && localHourDecimal <= sunsetHour) {
      isDaylight = true;
      if (localHourDecimal <= sunriseHour + goldenHourDuration ||
          localHourDecimal >= sunsetHour - goldenHourDuration) {
        sunPosition = "golden";
      } else {
        sunPosition = "day";
      }
    } else if (
      (localHourDecimal >= sunriseHour - twilightDuration && localHourDecimal < sunriseHour) ||
      (localHourDecimal > sunsetHour && localHourDecimal <= sunsetHour + twilightDuration)
    ) {
      isDaylight = false;
      sunPosition = "twilight";
    } else {
      isDaylight = false;
      sunPosition = "night";
    }
  }

  // Format sunrise/sunset times
  const formatHourToTime = (hour: number): string => {
    const h = Math.floor(hour);
    const m = Math.round((hour - h) * 60);
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  };

  // Calculate hours until sunset/sunrise
  let hoursUntilSunset: number | undefined;
  let hoursUntilSunrise: number | undefined;

  if (isDaylight) {
    hoursUntilSunset = sunsetHour - localHourDecimal;
    if (hoursUntilSunset < 0) hoursUntilSunset = undefined;
  } else {
    // If it's before sunrise today
    if (localHourDecimal < sunriseHour) {
      hoursUntilSunrise = sunriseHour - localHourDecimal;
    } else {
      // After sunset, sunrise is tomorrow
      hoursUntilSunrise = (24 - localHourDecimal) + sunriseHour;
    }
  }

  return {
    localTime,
    localTimeString: formatLocalTime(localTime),
    isDaylight,
    sunPosition,
    sunriseTime: formatHourToTime(sunriseHour),
    sunsetTime: formatHourToTime(sunsetHour),
    hoursUntilSunset,
    hoursUntilSunrise,
  };
};
